import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { quickLinksFor } from '@/features/help/quickLinks';
import { HELP_TOPICS } from '@/features/help/topics';

const FIRST_READ_TIPS = [
  {
    title: '左のメニュー',
    body: '画面の左にメニューがあります。やりたいことの名前をクリックすると、その画面が開きます。画面が狭いときは、左上のボタンでメニューが開きます。',
  },
  {
    title: '「使い方を見る」',
    body: '白い部分のいちばん上に、いま見ている画面の名前が出ます。その下の「使い方を見る」をクリックすると、このマニュアルの該当ページが開きます。',
  },
  {
    title: '青い文字と赤いボタン',
    body: '青い文字はリンクです。クリックすると別の画面に移ります。赤い文字のボタンを押すと、必ず確認の画面が出ます。確認するまで実行されません。',
  },
  {
    title: '赤い丸の意味',
    body: 'メニューの赤い丸は「まだ読んでいないお知らせ」です。作業の残り件数ではありません。',
  },
  {
    title: '探し方は2つ',
    body: '下の「やりたいことから探す」は、画面の名前が分からないときの入口です。「画面から探す」は左メニューと同じ名前です。',
  },
] as const;

/**
 * 操作マニュアルの一覧（画面ごとの独立ページへの入口）。
 * 各画面の詳しい使い方は `/help/:topicId`（HelpTopicPage）に分かれている。
 * 自分のロールが使わない画面のマニュアルは表示しない。権限バッジも出さない
 * （読み手は自分の操作だけを知りたい / 利用者フィードバック）。
 */
export function HelpIndexPage(): ReactElement {
  const { user } = useAuth();
  const myTopics = user ? HELP_TOPICS.filter((t) => t.roles.includes(user.active_role)) : [];
  const quickLinks = user ? quickLinksFor(user.active_role) : [];

  return (
    <>
      <PageHeader
        title="操作マニュアル"
        description="パソコンの操作に慣れていなくても使えるように、画面の開きかたから順に書いてあります。模式図と番号付きの箱で流れを示しています。飛ばさず上から読んでください。"
      />

      {user?.active_role === 'office' && (
        <section className="mb-4 rounded-sm border-2 border-wpaccent bg-white px-4 py-4">
          <h2 className="mb-1 font-semibold text-wptext-strong">イベント種別ごとの業務フロー</h2>
          <p className="mb-3 text-wptext-muted">
            月例会〜1on1について、「誰が・いつ・画面かリアルか・何をするか」を一覧にしています。事務局のみ閲覧できます（社外秘）。
          </p>
          <Link
            to="/help/event-ops-flows"
            className="inline-block rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
          >
            業務フロー表を開く
          </Link>
        </section>
      )}

      <section className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-4">
        <h2 className="mb-3 font-semibold text-wptext-strong">まず読むこと</h2>
        <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {FIRST_READ_TIPS.map((tip, index) => (
            <li
              key={tip.title}
              className="flex gap-3 rounded-sm border-2 border-wpaccent bg-wpcanvas/40 px-3 py-3"
            >
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-wpaccent text-sm font-semibold text-white"
              >
                {index + 1}
              </span>
              <div className="min-w-0">
                <p className="mb-1 font-semibold text-wptext-strong">{tip.title}</p>
                <p className="text-wptext-strong">{tip.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {quickLinks.length > 0 && (
        <section className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-4">
          <h2 className="mb-1 font-semibold text-wptext-strong">やりたいことから探す</h2>
          <p className="mb-3 text-wptext-muted">
            「手順を見る」はこのマニュアルの説明、「画面を開く」はその作業の画面です。
          </p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {quickLinks.map((link) => (
              <li
                key={link.question}
                className="flex flex-col gap-2 rounded-sm border border-wpborder bg-wpcanvas/40 px-3 py-3"
              >
                <span className="font-semibold text-wptext-strong">{link.question}</span>
                <span className="flex flex-wrap gap-3">
                  <Link
                    to={`/help/${link.helpTopicId}`}
                    className="rounded-sm border border-wpaccent bg-white px-2.5 py-1 text-wpaccent hover:underline"
                  >
                    手順を見る
                  </Link>
                  <Link
                    to={link.to}
                    className="rounded-sm border border-wpborder bg-white px-2.5 py-1 text-wpaccent hover:underline"
                  >
                    画面を開く
                  </Link>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-sm border border-wpborder bg-white px-4 py-4">
        <h2 className="mb-3 font-semibold text-wptext-strong">画面から探す</h2>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {myTopics.map((t) => (
            <li key={t.id}>
              <Link
                to={`/help/${t.id}`}
                className="flex h-full items-center rounded-sm border-2 border-wpaccent bg-wpcanvas/40 px-3 py-3 font-semibold text-wptext-strong hover:bg-white"
              >
                {t.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
